import React, { useState, useEffect } from 'react';
import Input from '../UI/Input';
import { useAppSelector } from '../../store/hooks';

interface EmptyCompanionInputProps {
  value: number;
  onChange: (count: number) => void;
  companionLimit?: number;
  detailedCompanionCount: number;
  disabled?: boolean;
  label?: string;
  className?: string;
  showError?: boolean;
}

export const EmptyCompanionInput: React.FC<EmptyCompanionInputProps> = ({
  value,
  onChange,
  companionLimit,
  detailedCompanionCount,
  disabled = false,
  label = 'Add empty companions',
  className = '',
  showError = true,
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [isEnabled, setIsEnabled] = useState(value > 0);
  const [error, setError] = useState<string | null>(null);

  // Update enabled state when value changes externally
  useEffect(() => {
    setIsEnabled(value > 0);
  }, [value]);

  const handleCheckboxChange = (checked: boolean) => {
    setIsEnabled(checked);
    if (!checked) {
      // Clear value when unchecked
      onChange(0);
      setError(null);
    }
  };

  const handleCountChange = (newCount: string) => {
    const numValue = parseInt(newCount, 10) || 0;
    
    // Validate
    if (numValue < 0) {
      setError('Count cannot be negative');
      return;
    }

    if (companionLimit !== undefined && numValue > companionLimit) {
      setError(`Cannot exceed companion limit of ${companionLimit}`);
      return;
    }

    // Check total companions (empty + detailed)
    const totalCompanions = numValue + detailedCompanionCount;
    if (companionLimit !== undefined && totalCompanions > companionLimit) {
      const available = companionLimit - detailedCompanionCount;
      setError(
        `Total companions (empty + detailed) cannot exceed ${companionLimit}. ` +
        `Currently: ${detailedCompanionCount} detailed. Available: ${available}`
      );
      return;
    }

    setError(null);
    onChange(numValue);
  };

  const maxValue = companionLimit !== undefined 
    ? Math.max(0, companionLimit - detailedCompanionCount)
    : 20;

  return (
    <div className={`empty-companion-input ${className}`}>
      <div className="flex items-center gap-3 mb-2">
        <input
          type="checkbox"
          id="empty-companion-checkbox"
          checked={isEnabled}
          onChange={(e) => handleCheckboxChange(e.target.checked)}
          disabled={disabled}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
        />
        <label
          htmlFor="empty-companion-checkbox"
          className={`text-sm font-medium cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {label}
        </label>
      </div>

      {isEnabled && (
        <div className="ml-7">
          <Input
            type="number"
            min={0}
            max={maxValue}
            value={value.toString()}
            onChange={(e) => handleCountChange(e.target.value)}
            disabled={disabled}
            placeholder="0"
            className="w-32"
          />
          {companionLimit !== undefined && (
            <p className="text-xs text-gray-500 mt-1">
              Max: {maxValue} (Limit: {companionLimit}, Detailed: {detailedCompanionCount})
            </p>
          )}
          {error && showError && (
            <p className="text-xs text-red-500 mt-1">{error}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default EmptyCompanionInput;


